<script lang="ts">
  import { onMount } from "svelte";
  import mousetrap from "mousetrap";
  import { a, getSelectedShapes, setMode, getNetElements } from "../../../shared/globalState/a";
  import { ObjectTypes } from "../../../shared/enums";
  import { Block } from "../../../models/shapes/Block";
  import { Line } from "../../../models/shapes/Line";
  import { Shape, TerminalPath } from "../../../shared/types";
  import { createNodeGraph, getNetworkLoops, assignTeeFlowDirections, getRelatedNodeFromFittingNode } from "../../../services/graph&network";
  import { ElbowBlock } from "../../../models/resistances/ElbowBlock";
  import { Tee } from "../../../models/resistances/Tee";
  import { DrawingModes } from "../../../shared/enums";
  import { Graf } from "../../../models/nets/GraphExtension";
  import { Branch } from "../../../models/nets/Branch";
  import { getVelocityRound, getVelocityRect } from "../../../services/calculations";
  import { drawShapes } from "../../../shared/render/shapes";
  import {
    performNetworkCalculations,
    filterBlockElements,
    assignNetworkParameters,
  } from "./ObjectProperties.calculations";
  import {
    createCalculationState,
    needsCalculationUpdate as checkNeedsCalculationUpdate,
    type CalculationState,
  } from "./ObjectProperties.state";
  import * as inputHandlers from "./ObjectProperties.inputHandlers";
  import DrawingMode from "./DrawingMode.svelte";

  /**
   * **********************************************
   * ** TODO:
   * -- выполняются расчёты разветвлённой сети --
   *    - требуется улучшение взаимодействия с UI
   *    - исправление ошибок если есть
   *    - проверка расчётов
   *    - проверка поведения при zoom/pan
   *    - улучшение отображения информации - при выборе всей сети отображать потери давления в расчётой ветви и выделять эту ветвь цветом
   * **********************************************
   */

  interface CrucialPoints {
    start?: any;
    end?: any;
    mid?: any;
  }

  // Interface definitions for resistance data
  interface LocalResistanceData {
    code?: string;
    angle?: number;
    kms: number;
    dp: number;
    // Tee-specific fields
    teeId?: string;
    exhaustOrSupply?: string;
    flowDirectionType?: string;
    isTee?: boolean;
  }

  interface ElemResistanceData {
    code: string;
    kms: number;
    dp: number;
  }

  // Interface for elements that can have graphs
  interface GraphableElement {
    getGraph?(): any;
    getCrucialPoints?(): { start: any; end: any };
  }

  // Interface for the result of createNodeGraph
  interface GraphWithTerminalPaths {
    graph: any;
    terminalPaths: TerminalPath[];
  }

  let isRound: boolean;
  let isRect: boolean;

  let diameter: number;
  let width: number;
  let height: number;
  let flowRate: number;

  let selectedNetElements: (Line | Block | ElbowBlock | Tee)[];
  let selectedSegments: Line[];
  let selectedAccessories: Block[];
  let selectedElbows: ElbowBlock[];
  let selectedTees: Tee[];
  let selectedAccessoriesAndElbows: (Block | ElbowBlock)[];
  let element: Line | Block | ElbowBlock | Tee | null;
  let elements: (Line | Block | ElbowBlock | Tee)[] | null;

  let show: boolean;
  let hasPendingChanges = false;
  let calledWithShortcut: boolean;
  let focusFlowRateInput: boolean;

  // Track last calculation state to detect if recalculation is needed
  let lastCalculationState: CalculationState | null = null;

  let singleSize: string | undefined;
  let singleFlow: string | undefined;
  let singleVelocity: string | undefined;
  let singleDp: string | undefined;
  let singleElemDp: string | undefined;
  let singleLen: string | undefined;
  let singleKms: number | undefined;
  let singleLocalDp: number | undefined;

  // Tee-specific display variables
  let teeFlowDirectionType: string | undefined;
  let teeExhaustOrSupply: string | undefined;
  let teeCollectorFlow: string | undefined;
  let teeTurnFlow: string | undefined;
  let teeCollectorSize: string | undefined;
  let teeTurnSize: string | undefined;
  let teeCollectorVelocity: string | undefined;
  let teeTurnVelocity: string | undefined;

  let commonSize: string | undefined;
  let commonFlow: string | undefined;
  let commonVelocity: string | undefined;
  let commonLenDp: string | undefined;
  let commonLen: string | undefined;
  let commonKms: number | undefined;
  let commonLocalDp: string | undefined;
  let commonElemDp: string | undefined;
  let commonSumDp: string | undefined;

  // Calculated branch info (for displaying when entire network is selected)
  let calculatedBranchId: string | null = null;
  let calculatedBranchDp: number | undefined = undefined;
  let calculatedBranchInfo: string | undefined = undefined;
  let isEntireNetworkSelected: boolean = false;

  let graphs: unknown[] = [];
  let loops: TerminalPath[] = [];

  // Performance optimization: Batch tee size updates
  let teeSizeUpdatePending: boolean = false;
  let teeSizeUpdateTimeout: number | null = null;

  // *** названия полей ***
  const SIZE_RECT_NAME = "Размер";
  const SIZE_ROUND_NAME = "Диаметр";
  const FLOW_RATE_NAME = "Расход";
  const VELOCITY_NAME = "Скорость";
  const DP_LEN_NAME = "ΔP длин.";
  const DP_LOCAL_NAME = "ΔP мест.";
  const DP_ELEM_NAME = "ΔP арм.";
  const DP_SUM_NAME = "ΔP общ.";
  const LEN_NAME = "Длина";
  const NOT_SPECIFIED_NAME = "Не указано";

  // *** units ***
  const LEN_UNIT = " м";
  const SIZE_UNIT = " мм";
  const FLOW_UNIT = " м³/ч";
  const VELOCITY_UNIT = " м/с";
  const DP_UNIT = " Па";

  const saveGraphs = (args: unknown[]): void => {
    graphs = args;
  };

  onMount(async function () {
    a.graph$.subscribe((v) => {
      saveGraphs(v as unknown[]);
    });

    // Subscribe to branches changes to update branch info when branches change
    a.branches$.subscribe((branches: Branch[]) => {
      // Mark that calculations may need update when branches change
      if (needsCalculationUpdate()) {
        hasPendingChanges = true;
      }

      // Update display when branches change (if elements are selected)
      if (selectedNetElements && selectedNetElements.length > 0) {
        const selectedBranchId = getSelectedBranchId();
        if (selectedBranchId) {
          // An entire branch is selected - update display
          updateDisplayValues(selectedNetElements);
        } else {
          // No entire branch selected - clear highlighting
          clearBranchHighlight();
          calculatedBranchId = null;
          calculatedBranchDp = undefined;
          calculatedBranchInfo = undefined;
        }
      }
    });

    // TODO: после операции breakPipe у вновь созданных отрезков не вычисляются потери давления по длине

    a.selected$.subscribe((selectedShapes: Shape[]) => {
      let fn = (s: Shape) => [ObjectTypes.line, ObjectTypes.block, ObjectTypes.elbowBlock, ObjectTypes.tee].includes(s.type);
      let fe = (s: Shape) => [ObjectTypes.line].includes(s.type);
      let fa = (s: Shape) => [ObjectTypes.block].includes(s.type);
      let fc = (s: Shape) => [ObjectTypes.elbowBlock].includes(s.type);
      let fz = (s: Shape) => [ObjectTypes.block, ObjectTypes.elbowBlock, ObjectTypes.tee].includes(s.type);

      selectedNetElements = (selectedShapes as (Line | Block | ElbowBlock | Tee)[]).filter((s) => fn(s));
      selectedSegments = (selectedShapes as Line[]).filter((s) => fe(s));
      selectedAccessories = (selectedShapes as Block[]).filter((s) => fa(s));
      selectedElbows = (selectedShapes as ElbowBlock[]).filter((s) => fc(s));
      let allSelectedTees = (selectedShapes as Tee[]).filter((s) => s.type === ObjectTypes.tee);

      // Check if entire branch is selected and filter tees accordingly
      const selectedBranchId = getSelectedBranchId();
      if (selectedBranchId) {
        // Filter tees to only those belonging to the selected branch
        selectedTees = allSelectedTees.filter((tee) => tee.net && tee.net.branchIds && tee.net.branchIds.includes(selectedBranchId));
      } else {
        // If no entire branch is selected, show all selected tees
        selectedTees = allSelectedTees;
      }

      selectedAccessoriesAndElbows = (selectedShapes as (Block | ElbowBlock)[]).filter((s) => fz(s));

      if (a.drawingMode === DrawingModes.duct) {
        // updateCalculations(selectedNetElements);
      }
      else if(a.drawingMode === DrawingModes.networkSketch){
        a.sigmaUpdate$.next(true);
      }

      // Update UI display for single element selection
      updateDisplayValues(selectedNetElements);

      // Check if calculations need update when selection changes
      // Reset pending changes flag only if calculations are up to date
      if (!needsCalculationUpdate()) {
        hasPendingChanges = false;
      } else {
        hasPendingChanges = true;
      }
      if (a.drawingMode === DrawingModes.networkSketch || a.drawingMode === DrawingModes.duct) {
        loops = getNetworkLoops(selectedSegments, graphs, a.sigmaInstance);
      }
    });

    // *** events

    a.commandsUI.callNetworkInfoWindow$.subscribe((v) => {
      show = v.show;
      calledWithShortcut = v.calledWithShortcut;
    });

    a.commandsUI.focusFlowRateInput$.subscribe((v) => {
      show = v.show;
      focusFlowRateInput = v.calledWithShortcut;

      // Trigger focus on flowRate input when called with shortcut
      if (v.calledWithShortcut) {
        setTimeout(() => {
          const flowRateInput = document.getElementById("flow-rate") as HTMLInputElement;
          if (flowRateInput) {
            flowRateInput.focus();
            flowRateInput.select();
          }
        }); // Small delay to ensure the input is rendered
      }
    });
  });

  function updateCalculations(netElements: (Line | Block | ElbowBlock | Tee)[]): void {
    if (!netElements || !netElements.length) {
      return;
    }

    // Perform network calculations
    performNetworkCalculations(netElements, flowRate);

    // Update display after calculations
    updateDisplayValues(netElements);

    // Update calculation state after successful calculation
    lastCalculationState = createCalculationState();
  }

  function updateDisplayValues(netElements: (Line | Block | ElbowBlock | Tee)[]): void {
    // Clear previous values
    clearDisplayValues();

    if (netElements.length === 0) return;

    // Check if entire network is selected (all branches)
    isEntireNetworkSelected = checkIfEntireNetworkSelected(netElements);

    // Check if any entire branch is selected
    const selectedBranchId = getSelectedBranchId();

    if (isEntireNetworkSelected) {
      // Entire network is selected - show calculated branch (main branch with max DP)
      // recalculate

      updateCalculatedBranchInfo();

      if (calculatedBranchId && a.branches) {
        const calculatedBranch = a.branches.find((b) => b.id === calculatedBranchId);
        if (calculatedBranch) {
          // Highlight the calculated branch
          highlightCalculatedBranch(calculatedBranchId);

          // Extract elements from calculated branch
          const branchSegments = calculatedBranch.elements.filter((e) => e instanceof Line) as Line[];
          const branchAccessories = calculatedBranch.elements.filter((e) => e instanceof Block && !(e instanceof ElbowBlock) && !(e instanceof Tee)) as Block[];
          const branchElbows = calculatedBranch.elements.filter((e) => e instanceof ElbowBlock) as ElbowBlock[];
          const allBranchTees = calculatedBranch.elements.filter((e) => e instanceof Tee) as Tee[];

          // Filter tees to only those belonging to the calculated branch (same logic as single branch selection)
          // A tee can belong to multiple branches, so we need to filter by branch ID
          const branchTees = allBranchTees.filter((tee) => tee.net && tee.net.branchIds && tee.net.branchIds.includes(calculatedBranchId));

          // Set selected elements BEFORE calling updateMultipleElementsDisplay (it uses them for calculations)
          selectedAccessories = branchAccessories;
          selectedElbows = branchElbows;
          selectedTees = branchTees;

          // Update display with calculated branch data
          updateMultipleElementsDisplay(calculatedBranch.elements, branchSegments, branchAccessories, branchElbows);

          // Override commonSumDp with branch.dp to ensure they match
          // branch.dp is the authoritative value calculated by Branch.calculateDp()
          commonSumDp = `${calculatedBranch.dp.toFixed(2)}${DP_UNIT}`;

          // Ensure local resistance data uses calculated branch ID (uses selectedElbows and selectedTees which are now from calculated branch)
          localResistanceData = getLocalResistancesData(calculatedBranchId);

          // Ensure element resistance data uses calculated branch accessories
          elemResistanceData = getElemResistanceData();

          return;
        }
      }
    } else if (selectedBranchId && a.branches) {
      // An entire branch is selected (but not entire network) - show branch alert and branch data
      // Note: selectedBranchId is only returned when selectedNetElements exactly matches branch.elements
      // So netElements === selectedBranch.elements, but we use netElements for consistency
      const selectedBranch = a.branches.find((b) => b.id === selectedBranchId);

      if (selectedBranch) {
        // Update calculated branch info for display (shows this branch's info)
        calculatedBranchId = selectedBranch.id;
        calculatedBranchDp = selectedBranch.dp;
        calculatedBranchInfo = `${selectedBranch.dp.toFixed(2)}${DP_UNIT}`;

        // Highlight the selected branch
        highlightCalculatedBranch(selectedBranchId);

        // Extract elements from selected elements (which equal branch.elements)
        // Use netElements directly since they're guaranteed to match branch.elements
        const branchSegments = netElements.filter((e) => e instanceof Line) as Line[];
        const branchAccessories = netElements.filter((e) => e instanceof Block && !(e instanceof ElbowBlock) && !(e instanceof Tee)) as Block[];
        const branchElbows = netElements.filter((e) => e instanceof ElbowBlock) as ElbowBlock[];
        // Deduplicate tees by ID to prevent duplicates in local resistance data
        const branchTeesMap = new Map<string, Tee>();
        netElements
          .filter((e) => e instanceof Tee)
          .forEach((tee) => {
            if (tee.id) {
              branchTeesMap.set(tee.id, tee as Tee);
            }
          });
        const branchTees = Array.from(branchTeesMap.values());

        // Set selected elements BEFORE calling updateMultipleElementsDisplay (it uses them for calculations)
        selectedAccessories = branchAccessories;
        selectedElbows = branchElbows;
        selectedTees = branchTees;

        // Update display with selected elements (which equal branch.elements)
        updateMultipleElementsDisplay(netElements, branchSegments, branchAccessories, branchElbows);

        // Override commonSumDp with branch.dp to ensure they match
        // branch.dp is the authoritative value calculated by Branch.calculateDp()
        commonSumDp = `${selectedBranch.dp.toFixed(2)}${DP_UNIT}`;

        // Ensure local resistance data uses selected branch ID (uses selectedElbows and selectedTees which are now from selected branch)
        localResistanceData = getLocalResistancesData(selectedBranchId);

        // Ensure element resistance data uses selected branch accessories
        elemResistanceData = getElemResistanceData();

        return;
      }
    } else {
      // No entire branch is selected and not entire network - clear highlighting and branch info
      isEntireNetworkSelected = false;
      clearBranchHighlight();
      calculatedBranchId = null;
      calculatedBranchDp = undefined;
      calculatedBranchInfo = undefined;
    }

    if (netElements.length === 1) {
      updateSingleElementDisplay(netElements[0]);
    } else {
      // Derive segments, accessories, elbows, and tees from netElements
      const segments = netElements.filter((e) => e instanceof Line) as Line[];
      const accessories = netElements.filter((e) => e instanceof Block && !(e instanceof ElbowBlock) && !(e instanceof Tee)) as Block[];
      const elbows = netElements.filter((e) => e instanceof ElbowBlock) as ElbowBlock[];
      // Deduplicate tees by ID to prevent duplicates in local resistance data
      const teesMap = new Map<string, Tee>();
      netElements
        .filter((e) => e instanceof Tee)
        .forEach((tee) => {
          if (tee.id) {
            teesMap.set(tee.id, tee as Tee);
          }
        });
      const tees = Array.from(teesMap.values());

      // Update module-level variables before calling updateMultipleElementsDisplay
      // (getLocalResistancesData uses selectedElbows and selectedTees, getElemResistanceData uses selectedAccessories)
      selectedAccessories = accessories;
      selectedElbows = elbows;
      selectedTees = tees;

      updateMultipleElementsDisplay(netElements, segments, accessories, elbows);
    }
  }

  /**
   * Check if entire network (all branches) is selected
   * @param netElements - Selected network elements
   * @returns true if all branches are selected
   */
  function checkIfEntireNetworkSelected(netElements: (Line | Block | ElbowBlock | Tee)[]): boolean {
    if (!a.branches || a.branches.length === 0 || netElements.length === 0) {
      return false;
    }

    // Get all element IDs from all branches
    const allBranchElementIds = new Set<string>();
    a.branches.forEach((branch) => {
      branch.elements.forEach((element) => {
        allBranchElementIds.add(element.id);
      });
    });

    // Check if all branch elements are in selected elements
    const selectedElementIds = new Set(netElements.map((el) => el.id));

    // All branch elements must be selected, and selected count should match
    return allBranchElementIds.size > 0 && allBranchElementIds.size === selectedElementIds.size && Array.from(allBranchElementIds).every((id) => selectedElementIds.has(id));
  }

  /**
   * Determine and update calculated branch information
   * The calculated branch is typically the one with maximum DP (critical path)
   */
  function updateCalculatedBranchInfo(): void {
    if (!a.branches || a.branches.length === 0) {
      calculatedBranchId = null;
      calculatedBranchDp = undefined;
      calculatedBranchInfo = undefined;
      return;
    }

    // Find branch with maximum DP (critical path)
    let maxDpBranch: Branch | null = null;
    let maxDp = -Infinity;

    a.branches.forEach((branch) => {
      const branchDp = branch.dp || 0;
      if (branchDp > maxDp) {
        maxDp = branchDp;
        maxDpBranch = branch;
      }
    });

    if (maxDpBranch) {
      calculatedBranchId = maxDpBranch.id;
      calculatedBranchDp = maxDpBranch.dp;
      calculatedBranchInfo = `${maxDpBranch.dp.toFixed(2)}${DP_UNIT}`;
    } else {
      calculatedBranchId = null;
      calculatedBranchDp = undefined;
      calculatedBranchInfo = undefined;
    }
  }

  /**
   * Highlight branch elements with red color via WebGL
   * Red color distinguishes the selected branch from normal selection (gray)
   * @param branchId - Branch ID to highlight (if null, uses calculatedBranchId)
   */
  function highlightCalculatedBranch(branchId?: string | null): void {
    const branchIdToHighlight = branchId || calculatedBranchId;
    if (!branchIdToHighlight || !a.branches) {
      return;
    }

    const branchToHighlight = a.branches.find((b) => b.id === branchIdToHighlight);

    if (!branchToHighlight) {
      return;
    }

    branchToHighlight.elements.forEach((element) => {
      // Mark element as belonging to highlighted branch
      // This flag will be checked in drawSingle to use red color instead of gray
      (element as any).isCalculatedBranch = true;
    });

    // Trigger redraw to apply WebGL color changes
    drawShapes();
  }

  /**
   * Clear branch highlighting and restore original state
   */
  function clearBranchHighlight(): void {
    if (!a.branches) {
      return;
    }

    a.branches.forEach((branch) => {
      branch.elements.forEach((element) => {
        // Remove calculated branch flag
        (element as any).isCalculatedBranch = false;
      });
    });

    // Trigger redraw
    drawShapes();
  }

  function clearDisplayValues(): void {
    element = null;
    elements = null;
    singleSize = undefined;
    singleFlow = undefined;
    singleVelocity = undefined;
    singleDp = undefined;
    singleElemDp = undefined;
    singleLen = undefined;

    // Clear tee-specific variables
    teeFlowDirectionType = undefined;
    teeExhaustOrSupply = undefined;
    teeCollectorFlow = undefined;
    teeTurnFlow = undefined;
    teeCollectorSize = undefined;
    teeTurnSize = undefined;
    teeCollectorVelocity = undefined;
    teeTurnVelocity = undefined;

    commonSize = undefined;
    commonFlow = undefined;
    commonVelocity = undefined;
    commonLenDp = undefined;
    commonLen = undefined;
    commonLocalDp = undefined;
    commonElemDp = undefined;
    commonSumDp = undefined;

    // Clear calculated branch info
    calculatedBranchId = null;
    calculatedBranchDp = undefined;
    calculatedBranchInfo = undefined;
    isEntireNetworkSelected = false;

    // Clear branch highlighting
    clearBranchHighlight();
  }

  /**
   * Log tee-specific information to console
   * @param tee - Tee element to extract information from
   */
  function printTeeInfo(tee: Tee): void {
    // Get flow direction type (use first branch if multiple)
    let flowDirectionType = "UNKNOWN";
    if (tee.net && tee.net.branchIds && tee.net.branchIds.length > 0) {
      const firstBranchId = tee.net.branchIds[0];
      const flowType = tee.flowDirectionType.get(firstBranchId);
      flowDirectionType = flowType || "UNKNOWN";
    }

    // Get exhaust/supply type
    let exhaustOrSupply = "UNKNOWN";
    if (tee.exhaustOrSupply === ObjectTypes.exhaust) {
      exhaustOrSupply = "Exhaust";
    } else if (tee.exhaustOrSupply === ObjectTypes.supply) {
      exhaustOrSupply = "Supply";
    }

    // Helper function to print connection info
    const printConnectionInfo = (connectionName: string, flowRate: number | undefined | null, width: number | undefined | null, height: number | undefined | null, shape: string): void => {
      // Flow rate
      if (flowRate !== undefined && flowRate !== null) {
      } else {
      }

      // Size
      if (shape === ObjectTypes.round) {
        if (width) {
        } else {
        }
      } else if (shape === ObjectTypes.rect) {
        if (width && height) {
        } else {
        }
      } else {
      }

      // Velocity
      if (shape === ObjectTypes.round && width && flowRate) {
        const velocity = getVelocityRound(width, flowRate);
      } else if (shape === ObjectTypes.rect && width && height && flowRate) {
        const velocity = getVelocityRect(width, height, flowRate);
      } else {
      }
    };

    // Calculate pass flow rate (collector - turn, conservation of flow)
    const passFlowRate = tee.flowRateCollectorCmh !== undefined && tee.flowRateCollectorCmh !== null && tee.flowRateTurnCmh !== undefined && tee.flowRateTurnCmh !== null ? tee.flowRateCollectorCmh - tee.flowRateTurnCmh : undefined;

    // Print Collector connection
    printConnectionInfo("Collector", tee.flowRateCollectorCmh, tee.widthCollectorMillimeter, tee.heightCollectorMillimeter, tee.shape);

    // Print Pass connection
    printConnectionInfo("Pass", passFlowRate, tee.widthPassMillimeter, tee.heightPassMillimeter, tee.shape);

    // Print Turn connection
    printConnectionInfo("Turn", tee.flowRateTurnCmh, tee.widthTurnMillimeter, tee.heightTurnMillimeter, tee.shape);
  }

  function updateSingleElementDisplay(selectedElement: Line | Block | ElbowBlock | Tee): void {
    if (!selectedElement.net) return;

    element = selectedElement;

    if (selectedElement instanceof Line) {
      singleSize = getSingleSize(selectedElement);
      singleLen = getSingleLen(selectedElement);
    }

    singleFlow = getSingleFlow(selectedElement);
    singleVelocity = getSingleVelocity(selectedElement);
    singleDp = getSingleDp(selectedElement);
    singleElemDp = getSingleElemDp(selectedElement);
  }

  function updateMultipleElementsDisplay(netElements: (Line | Block | ElbowBlock | Tee)[], segments: Line[], accessories: Block[], elbows: ElbowBlock[]): void {
    elements = netElements;

    commonSize = getCommonSize(netElements);
    commonFlow = getCommonFlow(netElements);
    commonVelocity = getCommonVelocity(netElements);
    commonLenDp = getCommonDp(segments);
    commonLen = getCommonLen(segments);
    commonLocalDp = getCommonLocalDp(elbows, selectedTees);
    commonElemDp = getCommonElemDp(accessories);
    commonSumDp = getCommonSumDp(netElements);
  }

  function handleRadioChange(event: Event): void {
    const changes = inputHandlers.handleRadioChange(event, { isRound, isRect, diameter, width, height, flowRate, calledWithShortcut, focusFlowRateInput, show } as any);
    if (changes.isRound !== undefined) isRound = changes.isRound;
    if (changes.isRect !== undefined) isRect = changes.isRect;
  }

  // ****** INPUT FIELDS EVENTS BINDING ******

  // Binding and focus functions - using imported functions from ObjectProperties.inputHandlers
  // These are reactive since they depend on component state
  $: focus = inputHandlers.createFocusHandler(calledWithShortcut, focusFlowRateInput);
  $: focusFlowRate = inputHandlers.createFocusFlowRateHandler(focusFlowRateInput);
  const bindEscapeInInputFields = inputHandlers.createEscapeBinding(handleEscapePress);
  const bindTabInInputFields = inputHandlers.createTabBinding();
  const bindEnterInInputFields = inputHandlers.createEnterBinding(
    assignNetParams,
    () => updateCalculations(selectedNetElements),
    (pending) => { hasPendingChanges = pending; },
    () => resetInputs()
  );

  /**
   * Resets all input field values to empty
   */
  function resetInputs(): void {
    inputHandlers.resetInputs(
      (rate) => { flowRate = rate; },
      (d) => { diameter = d; },
      (w) => { width = w; },
      (h) => { height = h; }
    );
  }

  /**
   * Handler for the update calculations button
   */
  function handleUpdateCalculations(): void {
    // Assign user-input values to network elements before calculations
    assignNetParams();

    // Update calculations
    updateCalculations(selectedNetElements);

    // Clear pending changes flag and update calculation state
    hasPendingChanges = false;
    updateLastCalculationState();

    // reset values in flowRate input, diameter input, width and height inputs
    resetInputs();
  }

  /**
   * Updates the last calculation state snapshot
   */
  function updateLastCalculationState(): void {
    lastCalculationState = createCalculationState();
  }

  /**
   * Checks if network calculations need to be updated
   * Returns true if recalculation is needed
   */
  function needsCalculationUpdate(): boolean {
    return checkNeedsCalculationUpdate(lastCalculationState, hasPendingChanges);
  }

  /**
   * Функция назначает элементам сети уже указанные параметры - расход, размеры, если таковые имеются
   *
   * В разветвленной сети:
   * - Расход назначается только терминалам (Block с checkIsTerminal())
   * - Расход для остальных элементов рассчитывается автоматически через Branch.assignCumulativeFlowRates()
   * - Размеры назначаются всем выбранным элементам (Line и Block)
   */
  function assignNetParams(): void {
    const selectedShapes = getSelectedShapes().filter((s: Shape) => s instanceof Line || s instanceof Block);
    assignNetworkParameters(selectedShapes, flowRate, isRect, isRound, width, height, diameter);
  }

  // Input handlers - using imported functions from ObjectProperties.inputHandlers
  function handleEscapePress(event: KeyboardEvent) {
    inputHandlers.handleEscapePress(
      event,
      (val) => { show = val; },
      (val) => { calledWithShortcut = val; },
      (val) => { focusFlowRateInput = val; }
    );
  }

  function handleDiameterInput(event: Event): void {
    inputHandlers.handleDiameterInput(event, diameter, selectedSegments, () => {
      hasPendingChanges = true;
    });
  }

  function handleWidthInput(event: Event): void {
    inputHandlers.handleWidthInput(event, width, selectedSegments, () => {
      hasPendingChanges = true;
    });
  }

  function handleHeightInput(event: Event): void {
    inputHandlers.handleHeightInput(event, height, selectedSegments, () => {
      hasPendingChanges = true;
    });
  }

  function handleFlowRateChange(event: Event): void {
    inputHandlers.handleFlowRateChange(
      event,
      (rate) => { flowRate = rate; },
      () => { hasPendingChanges = true; }
    );
  }


  // *** input events ***

  function getCommonSize(segments: (Line | Block | ElbowBlock | Tee)[]): string | undefined {
    if (segments.length === 0) return undefined;

    var etalon = segments[0];

    if (segments.every((s) => s.net)) {
      var widthAreEqual = segments.every((s) => etalon.net.widthMil && s.net.widthMil === etalon.net.widthMil);
      var heighAreEqual = segments.every((s) => s.net.heightMil === etalon.net.heightMil && etalon.net.heightMil);
      var diameterAreEqual = segments.every((s) => s.net.diameterMil === etalon.net.diameterMil && etalon.net.diameterMil);

      if (widthAreEqual && heighAreEqual) {
        return `${etalon.net.widthMil}x${etalon.net.heightMil}`;
      } else if (diameterAreEqual) {
        return `${etalon.net.diameterMil}`;
      } else {
        // Return undefined if values differ (don't show)
        return undefined;
      }
    }
    return undefined;
  }

  function getCommonFlow(segments: (Line | Block | ElbowBlock | Tee)[]): string | undefined {
    if (segments.length === 0) return undefined;

    var etalon = segments[0];
    if (segments.every((s) => s.net)) {
      var flowAreEqual = segments.every((s) => s.net.flowRateCmh === etalon.net.flowRateCmh && etalon.net.flowRateCmh);
      if (flowAreEqual) {
        let commonFlow = etalon.net.flowRateCmh;
        return `${commonFlow?.toFixed(2) + FLOW_UNIT}`;
      } else {
        // Return undefined if values differ (don't show)
        return undefined;
      }
    }
    return undefined;
  }

  function getCommonVelocity(segments: (Line | Block | ElbowBlock | Tee)[]): string | undefined {
    if (segments.length === 0) return undefined;

    var etalon = segments[0];
    if (segments.every((s) => s.net)) {
      var velocityAreEqual = segments.every((s) => s.net.velocity === etalon.net.velocity && etalon.net.velocity);
      if (velocityAreEqual) {
        let commonVelocity = etalon.net.velocity;
        return `${commonVelocity?.toFixed(2) + VELOCITY_UNIT}`;
      } else {
        // Return undefined if values differ (don't show)
        return undefined;
      }
    }
    return undefined;
  }

  function getCommonDp(segments: Line[]): string | undefined {
    if (segments.every((s) => s.net)) {
      var commonDp = segments.reduce((sum, seg) => sum + seg.net.dp, 0);
      if (commonDp) {
        return `${commonDp?.toFixed(2) + DP_UNIT}`;
      }
      return NOT_SPECIFIED_NAME;
    }
  }

  function getCommonLen(segments: Line[]): string | undefined {
    if (segments.every((s) => s.net)) {
      var commonLen = segments.reduce((sum, seg) => sum + seg.net.getRealLenMet(), 0);
      if (commonLen) {
        return `${commonLen?.toFixed(2) + LEN_UNIT}`;
      }
      return NOT_SPECIFIED_NAME;
    }
  }

  function getCommonLocalDp(elbows: ElbowBlock[], tees: Tee[] = []): string | undefined {
    let totalDp = 0;
    let hasValidData = false;

    // Sum DP from elbows
    if (elbows.length > 0 && elbows.every((elb) => elb.net)) {
      const elbowsDp = elbows.reduce((sum, el) => {
        const dp = el.net?.dp;
        if (typeof dp === "number" && !isNaN(dp) && isFinite(dp)) {
          return sum + dp;
        }
        return sum;
      }, 0);
      totalDp += elbowsDp;
      if (elbowsDp > 0) {
        hasValidData = true;
      }
    }

    // Sum DP from tees
    if (tees.length > 0 && tees.every((tee) => tee.net)) {
      // Use calculatedBranchId if available (for entire network case), otherwise getSelectedBranchId()
      const branchId = calculatedBranchId || getSelectedBranchId();
      const teesDp = tees.reduce((sum, tee) => {
        // If a specific branch is selected, use DP for that branch
        let dp: number | undefined;
        if (branchId && tee.branchDp?.has(branchId)) {
          dp = tee.branchDp.get(branchId);
        } else {
          // Otherwise, use net.dp (backward compatibility)
          dp = tee.net?.dp;
        }
        if (typeof dp === "number" && !isNaN(dp) && isFinite(dp)) {
          return sum + dp;
        }
        return sum;
      }, 0);
      totalDp += teesDp;
      if (teesDp > 0) {
        hasValidData = true;
      }
    }

    if (hasValidData && totalDp > 0) {
      return `${totalDp.toFixed(2)}${DP_UNIT}`;
    }
    return NOT_SPECIFIED_NAME;
  }

  function getCommonElemDp(accessories: Block[]): string | undefined {
    if (accessories.every((s) => s.net)) {
      var commonElemDp = accessories.reduce((sum, el) => {
        return sum + el.net.dp;
      }, 0);
      if (commonElemDp) {
        return `${commonElemDp?.toFixed(2) + DP_UNIT}`;
      }
      return NOT_SPECIFIED_NAME;
    }
  }

  function getCommonSumDp(segments: (Line | Block | ElbowBlock | Tee)[]): string | undefined {
    // Initialize variables to 0 to avoid NaN issues
    var clendp = 0;
    var clocaldp = 0;
    var celemDp = 0;

    if (commonLenDp) {
      clendp = parseFloat(commonLenDp.split(" ")[0]);
      // Проверка на NaN
      clendp = isNaN(clendp) ? 0 : clendp;
    }

    if (commonLocalDp) {
      clocaldp = parseFloat(commonLocalDp.split(" ")[0]);
      // Проверка на NaN
      clocaldp = isNaN(clocaldp) ? 0 : clocaldp;
    }

    if (commonElemDp) {
      celemDp = parseFloat(commonElemDp?.split(" ")[0]);
      // Проверка на NaN
      celemDp = isNaN(celemDp) ? 0 : celemDp;
    }

    // выбраны участки труб и арматура
    if (clendp || clocaldp || celemDp) {
      return `${(clendp + clocaldp + celemDp)?.toFixed(2) + DP_UNIT}`;
    }
    return NOT_SPECIFIED_NAME;
  }

  function getSingleSize(segment: Line): string {
    var isRect = segment.net.widthMil && segment.net.heightMil;
    var isRound = segment.net.diameterMil;
    if (isRect) {
      return `${segment.net.widthMil}x${segment.net.heightMil}`;
    } else if (isRound) {
      return `${segment.net.diameterMil}`;
    }
    return NOT_SPECIFIED_NAME;
  }

  function getSingleFlow(segment: Line | Block | ElbowBlock | Tee): string | undefined {
    if (segment.net.flowRateCmh) {
      let singleFlow = segment.net.flowRateCmh;
      return `${singleFlow?.toFixed(2) + FLOW_UNIT}`;
    }
    return NOT_SPECIFIED_NAME;
  }

  function getSingleVelocity(segment: Line | Block | ElbowBlock | Tee): string | undefined {
    if (segment.net.velocity) {
      let singleVelocity = segment.net.velocity;
      return `${singleVelocity?.toFixed(2) + VELOCITY_UNIT}`;
    }
    return NOT_SPECIFIED_NAME;
  }

  function getSingleDp(segment: Line | Block | ElbowBlock | Tee): string | undefined {
    if (segment.net.dp) {
      let singleDp = segment.net.dp;
      return `${singleDp?.toFixed(2) + DP_UNIT}`;
    }
    return NOT_SPECIFIED_NAME;
  }

  function getSingleElemDp(element: Block | ElbowBlock | Line) {
    if (element instanceof Line) return;

    if (element.net.dp) {
      let singleDp = element.net.dp;
      return `${singleDp?.toFixed(2) + DP_UNIT}`;
    }

    return NOT_SPECIFIED_NAME;
  }

  function getSingleLen(segment: Line): string | undefined {
    if (segment.net.getRealLenMet()) {
      // обновить длину отрезков
      let len = segment.getLength();
      segment.net.setRealLenMet(len, a.zlc);

      let singleLen = segment.net.getRealLenMet();
      return `${singleLen?.toFixed(2) + LEN_UNIT}`;
    }
    return NOT_SPECIFIED_NAME;
  }

  let showKms: boolean = false;
  let showElemSumDpDetails: boolean = false;

  /**
   * Safely extract ID from an element, handling both getter and direct property access
   * This is important for production builds where getters might not work correctly
   */
  function getElementId(element: any): string | null {
    if (!element) return null;

    // Try getter first (works in dev)
    if (element.id !== undefined && element.id !== null) {
      return String(element.id);
    }

    // Try direct property access (fallback for production)
    if (element._id !== undefined && element._id !== null) {
      return String(element._id);
    }

    return null;
  }

  /**
   * Check if entire branch is selected by comparing selected elements with branch elements
   * @returns Branch ID if entire branch is selected, null otherwise
   */
  function getSelectedBranchId(): string | null {
    if (!a.branches || a.branches.length === 0 || !selectedNetElements || selectedNetElements.length === 0) {
      return null;
    }

    // Create a set of selected element IDs for quick lookup
    // Use safe ID extraction for production build compatibility
    const selectedElementIds = new Set<string>();
    selectedNetElements.forEach((el) => {
      const id = getElementId(el);

      if (id) {
        selectedElementIds.add(id);
      }
    });

    if (selectedElementIds.size === 0) {
      return null;
    }

    // Check each branch to see if all its elements are selected
    for (const branch of a.branches) {
      if (!branch || !branch.elements || !Array.isArray(branch.elements) || branch.elements.length === 0) {
        continue;
      }

      // Get branch element IDs with safe extraction
      const branchElementIds = new Set<string>();
      let validBranchElements = 0;
      branch.elements.forEach((element) => {
        const id = getElementId(element);
        if (id) {
          branchElementIds.add(id);
          validBranchElements++;
        }
      });

      // Skip if no valid elements in branch
      if (validBranchElements === 0 || branchElementIds.size === 0) {
        continue;
      }

      // Check if all branch elements are in the selected set
      const allElementsSelected = Array.from(branchElementIds).every((elementId) => selectedElementIds.has(elementId));

      if (allElementsSelected && validBranchElements > 0) {
        // Also verify that selected elements match branch elements count exactly (to avoid false positives)
        // This ensures we have exactly the branch elements selected, not more, not less
        const selectedMatchBranch = validBranchElements === selectedElementIds.size && Array.from(branchElementIds).every((elementId) => selectedElementIds.has(elementId)) && Array.from(selectedElementIds).every((elementId) => branchElementIds.has(elementId));

        if (selectedMatchBranch) {
          return branch.id;
        }
      }
    }

    return null;
  }

  function toggleShowKms(e: Event): void {
    e.preventDefault();
    const selectedBranchId = getSelectedBranchId();
    localResistanceData = getLocalResistancesData(selectedBranchId);

    showKms = !showKms;
  }

  function toggleShowElemSumDpDetails(e: Event): void {
    e.preventDefault();
    elemResistanceData = getElemResistanceData();
    showElemSumDpDetails = !showElemSumDpDetails;
  }

  let localResistanceData: LocalResistanceData[] | undefined;
  let elemResistanceData: ElemResistanceData[] | undefined;

  /**
   * Format tee information for display in a human-readable, compact format
   * @param exhaustOrSupply - 'supply' or 'exhaust'
   * @param flowDirectionType - 'on pass' or 'on turn'
   * @param branchId - Branch ID (optional)
   * @returns Formatted string like "Приточный тройник на проход, ветвь branch-0"
   */
  /**
   * Translate branch ID for display purposes (branch -> Ветвь)
   * Only changes display, doesn't modify the actual branch.id
   * @param branchId - Branch ID like "branch-0"
   * @returns Translated string like "Ветвь-0"
   */
  function translateBranchIdForDisplay(branchId: string): string {
    return branchId.replace(/^branch/i, "Ветвь");
  }

  function formatTeeDisplayInfo(exhaustOrSupply: string, flowDirectionType: string, branchId?: string): string {
    const typeMap: Record<string, string> = {
      supply: "Приточный",
      exhaust: "Вытяжной",
      UNKNOWN: "Неизвестный",
    };

    const flowMap: Record<string, string> = {
      "on pass": "на проход",
      "on turn": "на поворот",
      UNKNOWN: "неизвестно",
    };

    const type = typeMap[exhaustOrSupply] || exhaustOrSupply;
    const flow = flowMap[flowDirectionType] || flowDirectionType;

    if (branchId) {
      return `${type} тройник ${flow}, ветвь ${branchId}`;
    }
    return `${type} тройник ${flow}`;
  }

  function getLocalResistancesData(selectedBranchId: string | null = null): LocalResistanceData[] | undefined {
    const data: LocalResistanceData[] = [];

    // Add elbows data
    if (selectedElbows.length > 0 && selectedElbows.every((el) => el.net)) {
      const elbowsData = selectedElbows
        .filter((el) => el instanceof ElbowBlock)
        .map((el) => {
          return {
            code: el.data.code,
            angle: el.data.angle,
            kms: el.net.kms,
            dp: el.net.dp,
            isTee: false,
          };
        });
      data.push(...elbowsData);
    }

    // Add tees data
    if (selectedTees.length > 0 && selectedTees.every((tee) => tee.net)) {
      // Remove duplicates by tee ID first (a tee might appear multiple times if it belongs to multiple branches)
      const uniqueTees = new Map<string, Tee>();
      selectedTees.forEach((tee) => {
        if (tee instanceof Tee && tee.id) {
          uniqueTees.set(tee.id, tee);
        }
      });

      // Filter tees: if a branch is selected, only include tees that belong to that branch
      const filteredTees = Array.from(uniqueTees.values()).filter((tee) => {
        // If a branch is selected, only include tees that belong to that branch
        if (selectedBranchId && tee.net && tee.net.branchIds) {
          return tee.net.branchIds.includes(selectedBranchId);
        }
        // If no branch selected, include all tees
        return true;
      });


      const teesData = filteredTees
        .map((tee) => {
          // Get exhaust/supply type
          const exhaustOrSupply = tee.exhaustOrSupply === ObjectTypes.exhaust ? "exhaust" : tee.exhaustOrSupply === ObjectTypes.supply ? "supply" : "UNKNOWN";

          // If a specific branch is selected, show only the tee's behavior for that branch
          // Note: filteredTees already contains only tees that belong to selectedBranchId, so this check should always pass
          if (selectedBranchId && tee.net && tee.net.branchIds && tee.net.branchIds.includes(selectedBranchId)) {
            const flowType = tee.flowDirectionType.get(selectedBranchId) || "UNKNOWN";
            if (!flowType || flowType === "UNKNOWN") {
              console.warn(`[Tee Flow Directions] Tee ${tee.id} has UNKNOWN or missing flowDirectionType for selected branch ${selectedBranchId}. Available flowDirectionTypes:`, Array.from(tee.flowDirectionType.entries()), `branchIds:`, tee.net.branchIds);
            }

            
            const displayInfo = formatTeeDisplayInfo(exhaustOrSupply, flowType, selectedBranchId);
            // Get DP and KMS for this specific branch
            const teeDp = tee.branchDp?.get(selectedBranchId) ?? (typeof tee.net.dp === "number" && !isNaN(tee.net.dp) && isFinite(tee.net.dp) ? tee.net.dp : 0);
            const teeKms = tee.branchKms?.get(selectedBranchId) ?? (typeof tee.net.kms === "number" && !isNaN(tee.net.kms) && isFinite(tee.net.kms) ? tee.net.kms : 0);
            return [
              {
                teeId: displayInfo,
                exhaustOrSupply: exhaustOrSupply,
                flowDirectionType: flowType,
                kms: teeKms,
                dp: teeDp,
                isTee: true,
              },
            ];
          }

          // This should not happen if selectedBranchId is provided (filteredTees already filtered)
          // But keep for safety when no branch is selected

          // Otherwise, show tee for all branches it belongs to
          let flowDirectionTypes: string[] = [];
          if (tee.net && tee.net.branchIds && tee.net.branchIds.length > 0) {
            const availableBranchIds = Array.from(tee.flowDirectionType.keys());
            flowDirectionTypes = tee.net.branchIds.map((branchId: string) => {
              const flowType = tee.flowDirectionType.get(branchId);
              if (!flowType || flowType === "UNKNOWN") {
                // Only warn if we have flowDirectionTypes but they don't match branchIds
                const availableFlowDirectionTypes = Array.from(tee.flowDirectionType.entries());
                console.warn(`[Tee Flow Directions] Tee ${tee.id} has UNKNOWN or missing flowDirectionType for branch ${branchId}.`, {
                  teeNetBranchIds: tee.net.branchIds,
                  availableBranchIds: availableBranchIds,
                  allAvailableTypes: availableFlowDirectionTypes,
                });
              }
              return flowType || "UNKNOWN";
            });
          } else {
            const availableFlowDirectionTypes = Array.from(tee.flowDirectionType.entries());
            if (availableFlowDirectionTypes.length > 0) {
              console.warn(`[Tee Flow Directions] Tee ${tee.id} has flowDirectionTypes but no branchIds in tee.net.branchIds:`, {
                availableFlowDirectionTypes,
                teeNetBranchIds: tee.net?.branchIds,
              });
            }
            flowDirectionTypes = ["UNKNOWN"];
          }

          // Create one entry per branch, or one entry if no branches
          if (flowDirectionTypes.length > 0) {
            return flowDirectionTypes.map((flowType, index) => {
              const branchId = tee.net?.branchIds?.[index] || "";
              const displayInfo = branchId ? formatTeeDisplayInfo(exhaustOrSupply, flowType, branchId) : formatTeeDisplayInfo(exhaustOrSupply, flowType);
              // Get DP and KMS for this specific branch
              const teeDp = branchId ? (tee.branchDp?.get(branchId) ?? (typeof tee.net.dp === "number" && !isNaN(tee.net.dp) && isFinite(tee.net.dp) ? tee.net.dp : 0)) : typeof tee.net.dp === "number" && !isNaN(tee.net.dp) && isFinite(tee.net.dp) ? tee.net.dp : 0;
              const teeKms = branchId ? (tee.branchKms?.get(branchId) ?? (typeof tee.net.kms === "number" && !isNaN(tee.net.kms) && isFinite(tee.net.kms) ? tee.net.kms : 0)) : typeof tee.net.kms === "number" && !isNaN(tee.net.kms) && isFinite(tee.net.kms) ? tee.net.kms : 0;
              return {
                teeId: displayInfo,
                exhaustOrSupply: exhaustOrSupply,
                flowDirectionType: flowType,
                kms: teeKms,
                dp: teeDp,
                isTee: true,
              };
            });
          } else {
            const teeDp = typeof tee.net.dp === "number" && !isNaN(tee.net.dp) && isFinite(tee.net.dp) ? tee.net.dp : 0;
            const teeKms = typeof tee.net.kms === "number" && !isNaN(tee.net.kms) && isFinite(tee.net.kms) ? tee.net.kms : 0;
            return [
              {
                teeId: formatTeeDisplayInfo(exhaustOrSupply, "UNKNOWN"),
                exhaustOrSupply: exhaustOrSupply,
                flowDirectionType: "UNKNOWN",
                kms: teeKms,
                dp: teeDp,
                isTee: true,
              },
            ];
          }
        })
        .flat(); // Flatten array of arrays
      data.push(...teesData);
    }

    return data.length > 0 ? data : undefined;
  }

  function getElemResistanceData(): ElemResistanceData[] | undefined {
    if (selectedAccessories.every((el) => el.net)) {
      var data = selectedAccessories
        .filter((el) => el.type === ObjectTypes.block)
        .map((el) => {
          return {
            code: el.data.code,
            kms: el.net.kms,
            dp: el.net.dp,
          };
        });
      return data;
    }
    return undefined;
  }

  // selectTextContent is now imported from ObjectProperties.inputHandlers
  const selectTextContent = inputHandlers.selectTextContent;

  /**
   * Calculate the flow rate for a specific branch
   * @param branch - Branch containing elements and end flow rate
   * @returns calculated flow rate for the branch
   */
  function getBranchFlowRate(branch: { id: string; elements: (Line | Block | ElbowBlock | Tee)[]; endFlowRate: number }): number {
    // For now, return the end flow rate
    // In a full implementation, this would calculate flow based on:
    // - Branch topology (series vs parallel)
    // - Shared elements with other branches
    // - Network flow distribution

    // Example: Check for shared elements
    const sharedElements = branch.elements.filter((element) => element.net.isSharedElement());

    return branch.endFlowRate;
  }

  /**
   * Example function demonstrating branch management methods
   * This shows how to use the new NetworkInfo branch functionality
   */
  function demonstrateBranchUsage(): void {
    // Example: Check which elements belong to specific branches
    const branchId = "branch-1";
    const elementsInBranch = selectedNetElements.filter((element) => element.net.belongsToBranch(branchId));

    // Example: Find shared elements
    const sharedElements = selectedNetElements.filter((element) => element.net.isSharedElement());

    // Example: Add element to a branch
    if (selectedNetElements.length > 0) {
      const element = selectedNetElements[0];
      element.net.addToBranch("demo-branch");

      // Clean up
      element.net.removeFromBranch("demo-branch");
    }
  }
</script>

<div class="container" on:mouseenter={() => (a.blockUI = true)} on:mouseleave={() => (a.blockUI = false)} role="dialog">
  <h1>Свойства объекта</h1>
  <!-- <small>Функция в разработке - пока можно использовать только для очень ориентировочных расчётов</small> -->
  <hr />

  {#if a.drawingMode === DrawingModes.duct}
    {#if selectedNetElements?.length || show}
      <div id="network-input-properties">
        <div>
          <input type="number" name="flow-rate" id="flow-rate" placeholder="Расход м³/ч ..." bind:value={flowRate} on:input={handleFlowRateChange} use:focusFlowRate use:bindEscapeInInputFields use:bindEnterInInputFields on:click={selectTextContent} />
        </div>
        <div>
          <input type="radio" name="round" id="round" checked={isRound} on:change={handleRadioChange} />
          <label for="round">Круглый</label>
        </div>
        <div>
          <input type="radio" name="rect" id="rect" checked={isRect} on:change={handleRadioChange} />
          <label for="rect">Прямоугольный</label>
        </div>
        {#if isRound}
          <div>
            <input type="number" name="round-diameter" id="round-diameter" placeholder="Диаметр ..." bind:value={diameter} on:input={handleDiameterInput} use:focus use:bindTabInInputFields use:bindEscapeInInputFields use:bindEnterInInputFields on:click={selectTextContent} />
          </div>
        {:else if isRect}
          <input type="number" name="rect-width" id="rect-width" placeholder="Ширина ..." bind:value={width} on:input={handleWidthInput} use:focus use:bindEscapeInInputFields use:bindEnterInInputFields on:click={selectTextContent} />
          <input type="number" name="rect-height" id="rect-height" placeholder="Высота ..." bind:value={height} on:input={handleHeightInput} use:bindTabInInputFields use:bindEscapeInInputFields use:bindEnterInInputFields on:click={selectTextContent} />
        {/if}
        <div style="margin-top: 0.5rem; display: flex; align-items: center;">
          <button
            type="button"
            on:click={handleUpdateCalculations}
            style="padding: 0.3rem 0.6rem; font-size: 0.8rem; background-color: {hasPendingChanges || needsCalculationUpdate() ? '#ffc107' : '#007bff'}; color: {hasPendingChanges || needsCalculationUpdate()
              ? '#000'
              : 'white'}; border: none; border-radius: 3px; cursor: pointer; transition: background-color 0.2s; font-weight: {hasPendingChanges || needsCalculationUpdate() ? 'bold' : 'normal'};"
            on:mouseenter={(e) => {
              const needsUpdate = hasPendingChanges || needsCalculationUpdate();
              e.currentTarget.style.backgroundColor = needsUpdate ? "#ff9800" : "#0056b3";
            }}
            on:mouseleave={(e) => {
              const needsUpdate = hasPendingChanges || needsCalculationUpdate();
              e.currentTarget.style.backgroundColor = needsUpdate ? "#ffc107" : "#007bff";
            }}
            title={hasPendingChanges || needsCalculationUpdate() ? "Требуется обновление расчётов (или нажмите Enter)" : "Обновить расчёты (или нажмите Enter)"}>
            ↻ Обновить (Enter)
          </button>
        </div>
      </div>

      <hr style="margin-top: 1em;" />
      {#if element}
        <div class="network-info">
          <div class="grid-row">
            <div class="key">{SIZE_RECT_NAME}</div>
            <div class="value">
              {singleSize ? singleSize : NOT_SPECIFIED_NAME}
            </div>
          </div>
          <div class="grid-row">
            <div class="key">{FLOW_RATE_NAME}</div>
            <div class="value">
              {singleFlow ? singleFlow : NOT_SPECIFIED_NAME}
            </div>
          </div>
          <div class="grid-row">
            <div class="key">{VELOCITY_NAME}</div>
            <div class="value">
              {singleVelocity ? singleVelocity : NOT_SPECIFIED_NAME}
            </div>
          </div>

          {#if element.type === ObjectTypes.line}
            <div class="grid-row">
              <div class="key">{DP_LEN_NAME}</div>
              <div class="value">
                {singleDp ? singleDp : NOT_SPECIFIED_NAME}
              </div>
            </div>
            <div class="grid-row">
              <div class="key">Длина</div>
              <div class="value">
                {singleLen ? singleLen : NOT_SPECIFIED_NAME}
              </div>
            </div>
          {:else}
            <div class="grid-row">
              <div class="key">{DP_ELEM_NAME}</div>
              <div class="value">
                {singleElemDp ? singleElemDp : NOT_SPECIFIED_NAME}
              </div>
            </div>
            <div class="grid-row">
              <div class="key">КМС</div>
              <div class="value">
                <input type="number" name="selected-element-kms" id="selected-element-kms" step="0.01" bind:value={element.net.kms} use:bindEnterInInputFields use:bindEscapeInInputFields on:click={selectTextContent} />
              </div>
            </div>
          {/if}
        </div>
      {:else if elements}
        <div class="network-info">
          {#if calculatedBranchInfo && calculatedBranchId}
            <div style="background-color: #fff3cd; padding: 8px; border-left: 4px solid #ffc107; margin-top: 8px; border-radius: 4px;">
              <div class="grid-row">
                <div class="key" style="font-weight: bold; color: #856404; white-space: nowrap;">{isEntireNetworkSelected ? "Расчётная " : ""}{translateBranchIdForDisplay(calculatedBranchId)}</div>
              </div>
              <div class="grid-row">
                <div class="key" style="font-weight: bold; color: #856404;">ΔP</div>
                <div class="value" style="font-weight: bold; color: #856404;">
                  {calculatedBranchInfo}
                </div>
              </div>
            </div>
          {/if}
          {#if commonSize}
            <div class="grid-row">
              <div class="key">{SIZE_RECT_NAME}</div>
              <div class="value">
                {commonSize}
              </div>
            </div>
          {/if}
          {#if commonFlow}
            <div class="grid-row">
              <div class="key">{FLOW_RATE_NAME}</div>
              <div class="value">
                {commonFlow}
              </div>
            </div>
          {/if}
          {#if commonVelocity}
            <div class="grid-row">
              <div class="key">{VELOCITY_NAME}</div>
              <div class="value">
                {commonVelocity}
              </div>
            </div>
          {/if}
          <div class="grid-row">
            <div class="key">{DP_LEN_NAME}</div>
            <div class="value">
              {commonLenDp ? commonLenDp : NOT_SPECIFIED_NAME}
            </div>
          </div>
          <div class="grid-row">
            <div class="key">{DP_LOCAL_NAME}</div>
            <a href="/" class="value link" on:click={toggleShowKms}>
              {commonLocalDp ? commonLocalDp : NOT_SPECIFIED_NAME}
            </a>
          </div>
          {#if showKms}
            <table class="table small">
              <thead>
                <tr>
                  <td>Тип</td>
                  <td>КМС</td>
                  <td>ΔP</td>
                </tr>
              </thead>
              <tbody>
                {#each localResistanceData as row}
                  <tr>
                    <td>
                      {#if row.isTee}
                        {row.teeId || "Неизвестный тройник"}
                      {:else}
                        {row.code + " " + row.angle?.toFixed(0)}
                      {/if}
                    </td>
                    <td>{row.kms?.toFixed(2)}</td>
                    <td>{row.dp?.toFixed(1)}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          {/if}
          <div class="grid-row">
            <div class="key">{DP_ELEM_NAME}</div>
            <a href="/" class="value link" on:click={toggleShowElemSumDpDetails}>
              {commonElemDp ? commonElemDp : NOT_SPECIFIED_NAME}
            </a>
          </div>

          {#if showElemSumDpDetails}
            <table class="table small">
              <thead>
                <tr>
                  <td>Тип</td>
                  <td>КМС</td>
                  <td>ΔP</td>
                </tr>
              </thead>
              <tbody>
                {#each elemResistanceData as row}
                  <tr>
                    <td>{row.code}</td>
                    <td>{row.kms?.toFixed(2)}</td>
                    <td>{row.dp?.toFixed(1)}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          {/if}
          <div class="grid-row">
            <div class="key"><strong>{DP_SUM_NAME}</strong></div>
            <div class="value">
              <strong>{commonSumDp ? commonSumDp : NOT_SPECIFIED_NAME}</strong>
            </div>
          </div>

          <div class="grid-row">
            <div class="key">{LEN_NAME}</div>
            <div class="value">
              {commonLen ? commonLen : NOT_SPECIFIED_NAME}
            </div>
          </div>
        </div>
      {/if}
    {/if}
  {:else if a.drawingMode === DrawingModes.networkSketch}
    <p>Всего контуров</p>
    <p>{loops?.length ?? 0}</p>
  {/if}
</div>

<style>
  h1 {
    font-size: large;
    font-weight: bold;
    text-align: center;
  }

  hr {
    margin-top: 0.25em;
    margin-bottom: 0.5em;
  }

  .container {
    border-radius: 5px;
    background-color: rgb(241 245 249 / 1);
    padding: 0.5em 1em;
    /* font-size: .8em; */
    width: 100%;
    height: calc(60vh + 28vh + 1em);
    overflow: auto;
    margin-top: 6px;
  }

  input[type="number"] {
    display: block;
    padding: 0 0.25em;
    width: 100%;
  }

  .grid-row {
    display: grid;
    grid-template-columns: 0.75fr 1fr;
    grid-template-rows: 1fr;
    column-gap: 0.25em;
  }

  .value {
    font-family: monospace;
  }

  .network-info {
    font-size: 1em;
  }

  .link {
    text-decoration: none;
    color: #3f47cc;
    font-weight: bold;
    border-bottom: 2px solid transparent; /* Set initial border to transparent with 0 width */
    display: inline-block;
    padding-bottom: 2px; /* Add padding to compensate for border width */
    transition: border-color 0.3s;
  }
</style>
